import { inject, Injectable, Injector } from '@angular/core';
import { DEFAULT_MAX_RESULT_COUNT, ZnSelectQueryParams } from '@zendra/shared';
import { InventoryDataEntryService, LidarInventoryDataEntryService } from 'inventory/proxy';
import { map, Observable, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class Catalog {
  private readonly injector = inject(Injector);
  private readonly service = inject(InventoryDataEntryService);
  public useLidar = false;

  loadMeditionNumbers(
    params?: ZnSelectQueryParams
  ): Observable<{ items: { key: string; value: string; }[]; totalCount: number }> {
    const search = params?.search?.trim();
    const filter = search && !Number.isNaN(Number(search)) ? search : undefined;

    if (this.useLidar) {
      return this.injector.get(LidarInventoryDataEntryService).getDistinctMeditionNumbers({
        skipCount: params?.skipCount,
        maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
        filter,
      }).pipe(
        map(result => ({
          items: result.items.map(item => ({
            key: String(item),
            value: String(item),
          })),
          totalCount: result.totalCount,
        })),
      );
    }

    return this.service.getDistinctMeditionNumbers({
      skipCount: params?.skipCount,
      maxResultCount: params?.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      filter,
    }).pipe(
      map(result => ({
        items: result.items.map(item => ({
          key: String(item),
          value: String(item),
        })),
        totalCount: result.totalCount,
      })),
    );
  }

  loadAllMeditionNumbers(search?: string): Observable<{ key: string; value: string }[]> {
    return this.loadAllMeditionNumberPages(search, 0, []);
  }

  private loadAllMeditionNumberPages(
    search: string | undefined,
    skipCount: number,
    accumulated: { key: string; value: string }[],
  ): Observable<{ key: string; value: string }[]> {
    const pageSize = 100;

    return this.loadMeditionNumbers({ search, skipCount, maxResultCount: pageSize }).pipe(
      switchMap(result => {
        const items = [...accumulated, ...result.items];
        const nextSkipCount = skipCount + result.items.length;

        if (result.items.length === 0 || nextSkipCount >= result.totalCount) {
          return of(items);
        }

        return this.loadAllMeditionNumberPages(search, nextSkipCount, items);
      }),
    );
  }
  
  loadContinuousInventoryStands(meditionNumber: number): Observable<{ key: string; value: string }[]> {
    return this.loadAllContinuousInventoryStandPages(meditionNumber, 0, []).pipe(
      map(standNumbers => [...new Set(standNumbers)]
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right))
        .map(standNumber => ({ key: standNumber, value: standNumber }))),
    );
  }

  private loadAllContinuousInventoryStandPages(
    meditionNumber: number,
    skipCount: number,
    accumulated: string[],
  ): Observable<string[]> {
    const pageSize = 1000;

    return this.service.getList({
      meditionNumber,
      skipCount,
      maxResultCount: pageSize,
    }).pipe(
      switchMap(result => {
        const currentStandNumbers = result.items
          .map(item => item.standNumber?.trim())
          .filter((standNumber): standNumber is string => !!standNumber);
        const standNumbers = [...accumulated, ...currentStandNumbers];
        const nextSkipCount = skipCount + result.items.length;

        if (result.items.length === 0 || nextSkipCount >= result.totalCount) {
          return of(standNumbers);
        }

        return this.loadAllContinuousInventoryStandPages(meditionNumber, nextSkipCount, standNumbers);
      }),
    );
  }

  loadLidarStands(meditionNumber: number): Observable<{ key: string; value: string }[]> {
    return this.injector.get(LidarInventoryDataEntryService).getList({
      meditionNumber,
      skipCount: 0,
      maxResultCount: 1000,
    }).pipe(
      map(result => [...new Set(result.items.map(item => item.standNumber.trim()))]
        .sort((left, right) => left.localeCompare(right))
        .map(standNumber => ({ key: standNumber, value: standNumber }))),
    );
  }
}
